import { createContext, useContext, useEffect, useState } from 'react';
import { 
  onAuthStateChanged,
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut
} from 'firebase/auth';
import { doc, getDoc, setDoc, onSnapshot, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { sealSensitiveDoc, unsealSensitiveDoc } from '../utils/security';
import { AlertCircle, ShieldCheck, X } from 'lucide-react';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPasscodeVerified, setIsPasscodeVerified] = useState(false);
  const [sessionConflict, setSessionConflict] = useState(false);
  const [reclaimNic, setReclaimNic] = useState('');
  const [reclaimError, setReclaimError] = useState('');

  async function signup(email, password, phone, passcode, displayName, company, nicNumber, role = 'user') {
    const res = await createUserWithEmailAndPassword(auth, email, password);
    const user = res.user;
    
    const sessionId = Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem('loanme_session_id', sessionId);

    const userData = {
      uid: user.uid,
      email,
      phoneNumber: phone,
      passcode, // 4-digit PIN for 2FA
      displayName,
      company,
      personal: {
        nicNumber,
        mobileNumber: phone
      },
      role,
      sessionId,
      createdAt: new Date().toISOString()
    };

    await setDoc(doc(db, 'users', user.uid), sealSensitiveDoc(userData));
    setUserData(userData); // Keep plain in state
    return res;
  }

  async function login(identifer, password) {
    let res;
    // If identifier looks like an email, use it directly
    if (identifer.includes('@')) {
       res = await signInWithEmailAndPassword(auth, identifer, password);
    } else {
       // It's a phone number, find the email first
       const usersRef = collection(db, 'users');
       const q = query(usersRef, where('phoneNumber', '==', identifer));
       const querySnapshot = await getDocs(q);
       
       if (querySnapshot.empty) {
         throw new Error('No account found with this phone number.');
       }
       
       const userDocData = querySnapshot.docs[0].data();
       res = await signInWithEmailAndPassword(auth, userDocData.email, password);
    }

    const sessionId = Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem('loanme_session_id', sessionId);

    // Update session ID in Firestore
    const userRef = doc(db, 'users', res.user.uid);
    const userSnapshot = await getDoc(userRef);
    if (userSnapshot.exists()) {
      const currentData = unsealSensitiveDoc(userSnapshot.data());
      currentData.sessionId = sessionId;
      await setDoc(userRef, sealSensitiveDoc(currentData));
    }

    return { ...res, userData: unsealSensitiveDoc(userSnapshot.data()) };
  }

  async function reclaimSession() {
    if (!userData || !reclaimNic) return;
    
    // The NIC in userData from Firestore is already unsealed by the listener
    if (userData.personal?.nicNumber?.toUpperCase() === reclaimNic.toUpperCase()) {
      const newSessionId = Math.random().toString(36).substring(2) + Date.now().toString(36);
      localStorage.setItem('loanme_session_id', newSessionId);
      
      const userRef = doc(db, 'users', currentUser.uid);
      const updatedData = { ...userData, sessionId: newSessionId };
      await setDoc(userRef, sealSensitiveDoc(updatedData));
      
      setSessionConflict(false);
      setReclaimNic('');
      setReclaimError('');
    } else {
      setReclaimError('NIC Number verification failed. Please check and try again.');
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setIsPasscodeVerified(false);
      if (user) {
        const docRef = doc(db, 'users', user.uid);
        const unsubDoc = onSnapshot(docRef, (docSnap) => {
          if (docSnap.exists()) {
            const data = unsealSensitiveDoc(docSnap.data());
            const localSessionId = localStorage.getItem('loanme_session_id');
            
            // SINGLE SESSION CHECK: If session ID in DB changed, it means another device logged in
            if (data.role !== 'admin' && data.sessionId && localSessionId && data.sessionId !== localSessionId) {
                setSessionConflict(true);
                setUserData(data); // Update data so we can check NIC
                return;
            }

            // If no session ID in localStorage but it's in DB (initial load on same device)
            if (!localSessionId && data.sessionId) {
              localStorage.setItem('loanme_session_id', data.sessionId);
            }

            setUserData(data);
          } else {
            // User authenticated in Firebase but no profile document found
            signOut(auth);
          }
          setLoading(false);
        }, (error) => {
          console.error('Firestore Auth Snapshot failed:', error);
          signOut(auth);
          setLoading(false);
        });

        // 5-MINUTE AUTO-LOGOUT ON INACTIVITY
        let logoutTimer;
        const resetTimer = () => {
           if (logoutTimer) clearTimeout(logoutTimer);
           logoutTimer = setTimeout(() => {
              console.log('Inactivity timeout reached (5m). Logging out.');
              signOut(auth);
              setIsPasscodeVerified(false);
           }, 5 * 60 * 1000); // 5 minutes in milliseconds
        };

        const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
        activityEvents.forEach(evt => window.addEventListener(evt, resetTimer));
        
        // Start initial timer
        resetTimer();

        return () => {
           unsubDoc();
           if (logoutTimer) clearTimeout(logoutTimer);
           activityEvents.forEach(evt => window.removeEventListener(evt, resetTimer));
        };
      } else {
        setUserData(null);
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userData,
    signup,
    login,
    logout: async () => {
      setIsPasscodeVerified(false);
      localStorage.removeItem('loanme_session_id');
      await signOut(auth);
    },
    isAdmin: userData?.role === 'admin',
    isPasscodeVerified,
    setPasscodeVerified: (status) => setIsPasscodeVerified(status),
    verifyPasscode: (code) => {
       const isValid = userData?.passcode === code;
       if (isValid) setIsPasscodeVerified(true);
       return isValid;
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}

      {/* Session Conflict Modal */}
      {sessionConflict && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="text-amber-600 w-8 h-8" />
              </div>
              <h3 className="text-xl font-black text-stripe-dark mb-2">Session Conflict</h3>
              <p className="text-stripe-slate text-sm leading-relaxed mb-6">
                Your account was just logged in on another device. For security, only one device is allowed at a time.
              </p>
              
              <div className="space-y-4">
                <div className="text-left">
                  <label className="text-[10px] font-black text-stripe-slate uppercase tracking-widest pl-1 mb-1 block">Verify NIC Number</label>
                  <input 
                    type="text" 
                    placeholder="Enter your NIC to reclaim session"
                    className="w-full px-4 py-3 bg-stripe-bg border border-stripe-border rounded-xl font-bold text-stripe-dark focus:ring-2 focus:ring-stripe-blue/20 outline-none transition-all uppercase"
                    value={reclaimNic}
                    onChange={(e) => {
                      setReclaimNic(e.target.value);
                      setReclaimError('');
                    }}
                  />
                  {reclaimError && <p className="text-rose-500 text-[10px] mt-1 font-bold pl-1">{reclaimError}</p>}
                </div>

                <div className="flex flex-col gap-2">
                  <button 
                    onClick={reclaimSession}
                    disabled={!reclaimNic}
                    className="w-full py-4 bg-stripe-dark text-white rounded-xl font-black flex items-center justify-center gap-2 hover:bg-black transition-all disabled:opacity-50"
                  >
                    <ShieldCheck size={18} /> Reclaim Login
                  </button>
                  <button 
                    onClick={value.logout}
                    className="w-full py-3 bg-white text-stripe-slate hover:text-rose-500 rounded-xl font-bold text-xs transition-colors border border-transparent hover:border-rose-100"
                  >
                    Logout this device
                  </button>
                </div>
              </div>
            </div>
            <div className="bg-stripe-bg p-4 text-[10px] text-center text-stripe-slate font-medium border-t border-stripe-border">
              Reclaiming this session will log out the other device.
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
